import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Anchor,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    Paper,
    ScrollArea,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconSettings, IconUpload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { importCases, listOrganizations, previewCaseImport } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { PageHeader } from '../../components/PageHeader';
import { CASE_TYPE_VALUES } from '../../types/domain';
import type {
    CaseImportPreviewResult,
    CaseImportResult,
    CaseImportRowPreview,
    CaseImportRowStatus,
    CreatedCaseProjection,
} from '../../types/api';

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

/** Outcome tiles, ordered worst-last so the eye lands on failures. */
const OUTCOME_TILES = [
    { key: 'created', color: 'green' },
    { key: 'duplicate', color: 'yellow' },
    { key: 'skipped', color: 'gray' },
    { key: 'failed', color: 'red' },
] as const;

/** Rows that can never be created, so their checkboxes stay disabled. */
const NON_SELECTABLE_STATUSES: CaseImportRowStatus[] = ['update', 'duplicate', 'parse_error'];

const STATUS_COLORS: Record<CaseImportRowStatus, string> = {
    create: 'green',
    update: 'blue',
    duplicate: 'yellow',
    missing_data: 'orange',
    parse_error: 'red',
};

function isAcceptedFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Rows that can be created and are not pre-selected (missing data defaults to unchecked). */
function isSelectable(row: CaseImportRowPreview): boolean {
    return !NON_SELECTABLE_STATUSES.includes(row.status);
}

function defaultSelection(rows: CaseImportRowPreview[]): Set<number> {
    return new Set(
        rows.filter((row) => isSelectable(row) && row.status !== 'missing_data').map((row) => row.index),
    );
}

export function UploadCasesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [executionOrganizationId, setExecutionOrganizationId] = useState<string | null>(null);
    const [caseType, setCaseType] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [dragging, setDragging] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [preview, setPreview] = useState<CaseImportPreviewResult | null>(null);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [result, setResult] = useState<CaseImportResult | null>(null);

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    // Convention: Remote Gravity is the platform's default execution org.
    // Pre-select it so uploaded cases are commissioned for field work unless
    // the operator explicitly clears or changes the value.
    useEffect(() => {
        if (executionOrganizationId || !orgsQ.data?.data) {
            return;
        }
        const remoteGravity = orgsQ.data.data.find(
            (o) => o.name.toLowerCase() === 'remote gravity',
        );
        if (remoteGravity) {
            setExecutionOrganizationId(remoteGravity.id);
        }
    }, [executionOrganizationId, orgsQ.data]);

    const previewMutation = useMutation({
        mutationFn: () =>
            previewCaseImport({
                organizationId: organizationId as string,
                executionOrganizationId: executionOrganizationId ?? undefined,
                file: file as File,
            }),
        onSuccess: (data) => {
            setPreview(data);
            setSelected(defaultSelection(data.rows));
            setResult(null);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const importMutation = useMutation({
        mutationFn: () =>
            importCases({
                organizationId: organizationId as string,
                executionOrganizationId: executionOrganizationId ?? undefined,
                caseType: caseType as string,
                file: file as File,
                rows: Array.from(selected),
            }),
        onSuccess: (data) => {
            setResult(data);
            if (data.created > 0) {
                void queryClient.invalidateQueries({ queryKey: ['cases'] });
            }
            notifications.show({
                color: data.failed > 0 ? 'yellow' : 'green',
                message: t('features.cases.import.completed', { count: data.created }),
            });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const resetSelectionInputs = () => {
        setFile(null);
        setFileError(null);
        setPreview(null);
        setSelected(new Set());
        setResult(null);
    };

    const acceptFile = (candidate: File | undefined | null) => {
        if (!candidate) {
            return;
        }
        if (!isAcceptedFile(candidate)) {
            resetSelectionInputs();
            setFileError(t('features.cases.import.unsupportedFile'));
            return;
        }
        setFileError(null);
        setFile(candidate);
        setPreview(null);
        setSelected(new Set());
        setResult(null);
    };

    const toggleRow = (index: number) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const canPreview = Boolean(organizationId && caseType && file) && !previewMutation.isPending;
    const createableCount = preview?.rows.filter((row) => isSelectable(row)).length ?? 0;
    const canCommit =
        Boolean(preview && caseType) && selected.size > 0 && !importMutation.isPending;

    const continueToMonitoring = () => {
        const createdCases: CreatedCaseProjection[] = result?.createdCases ?? [];
        navigate('/cases/monitoring-settings', { state: { cases: createdCases } });
    };

    return (
        <Stack gap="lg">
            <Anchor
                component="button"
                type="button"
                c="dimmed"
                fz="sm"
                fw={600}
                onClick={() => navigate('/cases')}
                style={{ alignSelf: 'flex-start' }}
            >
                <Group gap={6}>
                    <IconArrowLeft size={15} />
                    {t('common.actions.back')}
                </Group>
            </Anchor>

            <PageHeader
                title={t('features.cases.import.title')}
                description={t('features.cases.import.description')}
            />

            {!preview ? (
                <DataPanel>
                    <Stack>
                        <Group grow align="flex-start" maw={520}>
                            <Select
                                label={t('fields.organization')}
                                required
                                searchable
                                data={orgOptions}
                                value={organizationId}
                                onChange={setOrganizationId}
                            />
                            <Select
                                label={t('fields.caseType')}
                                required
                                searchable
                                data={CASE_TYPE_VALUES.map((value) => ({
                                    value,
                                    label: t(`caseTypes.${value}`),
                                }))}
                                value={caseType}
                                onChange={setCaseType}
                            />
                        </Group>

                        <Select
                            label={t('fields.executionOrganization')}
                            searchable
                            clearable
                            data={orgOptions}
                            value={executionOrganizationId}
                            onChange={setExecutionOrganizationId}
                            description={t('fields.executionOrganizationDescription')}
                            maw={520}
                        />

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_EXTENSIONS.join(',')}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                acceptFile(e.currentTarget.files?.[0]);
                                // Allow re-selecting the same file after a failed import.
                                e.currentTarget.value = '';
                            }}
                        />

                        <Paper
                            withBorder
                            radius="md"
                            p="xl"
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                acceptFile(e.dataTransfer.files?.[0]);
                            }}
                            style={{
                                borderStyle: 'dashed',
                                borderWidth: 2,
                                textAlign: 'center',
                                transition: 'background-color 120ms ease',
                            }}
                            bg={dragging ? 'var(--mantine-color-blue-light)' : undefined}
                        >
                            <Stack gap={6} align="center">
                                <IconUpload
                                    size={28}
                                    stroke={1.6}
                                    color="var(--mantine-color-dimmed)"
                                />
                                <Text fw={600}>
                                    {file ? file.name : t('features.cases.import.dropzoneTitle')}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {file
                                        ? formatBytes(file.size)
                                        : t('features.cases.import.requiredColumnsBody')}
                                </Text>
                                <Anchor
                                    size="xs"
                                    href="/monitoring-upload-example.csv"
                                    download
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('features.cases.import.downloadExample')}
                                </Anchor>
                                <Button
                                    variant="light"
                                    mt="xs"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {file
                                        ? t('features.cases.import.chooseDifferentFile')
                                        : t('features.cases.import.chooseFile')}
                                </Button>
                            </Stack>
                        </Paper>

                        {fileError ? (
                            <Alert color="red" variant="light">
                                {fileError}
                            </Alert>
                        ) : null}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => navigate('/cases')}>
                                {t('common.actions.cancel')}
                            </Button>
                            <Button
                                loading={previewMutation.isPending}
                                disabled={!canPreview}
                                onClick={() => previewMutation.mutate()}
                            >
                                {t('features.cases.import.previewAction')}
                            </Button>
                        </Group>
                    </Stack>
                </DataPanel>
            ) : null}

            {preview ? (
                <DataPanel>
                    <Stack>
                        <Group justify="space-between" align="center">
                            <Stack gap={2}>
                                <Text fw={700} size="md">
                                    {t('features.cases.import.previewTitle', {
                                        fileName: file?.name ?? '',
                                    })}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {t('features.cases.import.previewDescription')}
                                </Text>
                            </Stack>
                            <Button
                                variant="light"
                                onClick={() => {
                                    resetSelectionInputs();
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                    }
                                }}
                            >
                                {t('features.cases.import.chooseDifferentFile')}
                            </Button>
                        </Group>

                        <ScrollArea.Autosize mah={480}>
                            <Table withTableBorder striped stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th style={{ width: 40 }} />
                                        <Table.Th>{t('features.cases.import.columns.status')}</Table.Th>
                                        <Table.Th>{t('features.cases.import.columns.loanId')}</Table.Th>
                                        <Table.Th>{t('features.cases.import.columns.name')}</Table.Th>
                                        <Table.Th>{t('features.cases.import.columns.phone')}</Table.Th>
                                        <Table.Th>{t('features.cases.import.columns.address')}</Table.Th>
                                        <Table.Th>{t('features.cases.import.columns.missing')}</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {preview.rows.map((row) => {
                                        const selectable = isSelectable(row);
                                        return (
                                            <Table.Tr key={row.index}>
                                                <Table.Td>
                                                    <Checkbox
                                                        checked={selected.has(row.index)}
                                                        disabled={!selectable}
                                                        onChange={() => toggleRow(row.index)}
                                                        aria-label={`row-${row.index}`}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge
                                                        variant="light"
                                                        color={STATUS_COLORS[row.status]}
                                                    >
                                                        {t(
                                                            `features.cases.import.statuses.${row.status}`,
                                                        )}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    {row.loanId ?? t('common.empty')}
                                                </Table.Td>
                                                <Table.Td>
                                                    {row.fullName ?? row.businessName ?? t('common.empty')}
                                                </Table.Td>
                                                <Table.Td>{row.phone ?? t('common.empty')}</Table.Td>
                                                <Table.Td>
                                                    {row.address ?? t('common.empty')}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4}>
                                                        {row.missingFields.length
                                                            ? row.missingFields.map((field) => (
                                                                  <Badge
                                                                      key={field}
                                                                      size="xs"
                                                                      variant="light"
                                                                      color="orange"
                                                                  >
                                                                      {t(
                                                                          `features.cases.import.missingFieldLabels.${field}`,
                                                                      )}
                                                                  </Badge>
                                                              ))
                                                            : null}
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea.Autosize>

                        <Group justify="flex-end">
                            <Text size="sm" c="dimmed">
                                {t('features.cases.import.rowsSelected', {
                                    count: selected.size,
                                    total: createableCount,
                                })}
                            </Text>
                            <Button
                                loading={importMutation.isPending}
                                disabled={!canCommit}
                                onClick={() => importMutation.mutate()}
                            >
                                {t('features.cases.import.commitAction', {
                                    count: selected.size,
                                })}
                            </Button>
                        </Group>
                    </Stack>
                </DataPanel>
            ) : null}

            {result ? (
                <DataPanel>
                    <Stack gap="sm">
                        <Text fw={600} size="sm">
                            {t('features.cases.import.resultTitle', {
                                fileName: result.fileName ?? file?.name ?? '',
                            })}
                        </Text>
                        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                            {OUTCOME_TILES.map((tile) => (
                                <Card
                                    key={tile.key}
                                    padding="sm"
                                    radius="md"
                                    bg={`var(--mantine-color-${tile.color}-light)`}
                                >
                                    <Stack gap={2} align="center">
                                        <Text
                                            fw={800}
                                            size="xl"
                                            c={`var(--mantine-color-${tile.color}-light-color)`}
                                        >
                                            {result[tile.key]}
                                        </Text>
                                        <Text
                                            size="xs"
                                            tt="uppercase"
                                            fw={700}
                                            c={`var(--mantine-color-${tile.color}-light-color)`}
                                        >
                                            {t(`features.cases.import.outcomes.${tile.key}`)}
                                        </Text>
                                    </Stack>
                                </Card>
                            ))}
                        </SimpleGrid>

                        {result.errors?.length ? (
                            <ScrollArea.Autosize mah={260}>
                                <Table withTableBorder striped stickyHeader>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>{t('features.cases.import.row')}</Table.Th>
                                            <Table.Th>{t('fields.externalReference')}</Table.Th>
                                            <Table.Th>{t('features.cases.import.reason')}</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {result.errors.map((rowError) => (
                                            <Table.Tr key={`${rowError.row}-${rowError.reason}`}>
                                                <Table.Td>{rowError.row}</Table.Td>
                                                <Table.Td>
                                                    {rowError.externalReferenceId ?? t('common.empty')}
                                                </Table.Td>
                                                <Table.Td>{rowError.reason}</Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea.Autosize>
                        ) : null}

                        <Group justify="flex-end">
                            {result.created > 0 ? (
                                <Button
                                    leftSection={<IconSettings size={16} />}
                                    onClick={continueToMonitoring}
                                >
                                    {t('features.cases.import.continueToMonitoring')}
                                </Button>
                            ) : null}
                            <Button
                                variant="light"
                                onClick={() => {
                                    resetSelectionInputs();
                                }}
                            >
                                {t('features.cases.import.uploadAnother')}
                            </Button>
                        </Group>
                    </Stack>
                </DataPanel>
            ) : null}
        </Stack>
    );
}
